import { createContext, useRef, useCallback, useContext, useEffect } from 'react'

import { REGIONS } from './telemetryEvents'
import { resolveNode } from './resolveNode'

// Study-side twin of component/workspace/context/RegionContext + hooks/useRegion.
//
// One deliberate change: rects are normalised against the IDE container element,
// not against window.innerWidth/innerHeight. The workspace can normalise regions
// to the window because .ws-content IS the viewport there, so the two frames
// coincide. In the study IDE the body sits between a top bar and a bottom bar,
// so window-normalised region rects would not line up with the container-
// normalised mouse coordinates and every `region` field would be skewed.
// Both sides of the lookup use the same frame here.
export const StudyRegionContext = createContext()

export const StudyRegionProvider = ({ containerRef, children }) => {
  const regionsRef = useRef({})

  // 1. Components call this to report their position
  const updateRegion = useCallback((id, rect) => {
    regionsRef.current[id] = rect
  }, [])

  // 2. Components call this when they unmount
  const removeRegion = useCallback((id) => {
    delete regionsRef.current[id]
  }, [])

  // 3. The "Lookup" function for the telemetry
  const getRegionName = useCallback((x, y) => {
    let bestMatch = REGIONS.BACKGROUND
    let maxPriority = -1

    const mouseX = Number(x)
    const mouseY = Number(y)

    for (const [name, rect] of Object.entries(regionsRef.current)) {
      if (
        mouseX >= rect.left &&
        mouseX <= rect.right &&
        mouseY >= rect.top &&
        mouseY <= rect.bottom
      ) {
        if (rect.priority > maxPriority) {
          maxPriority = rect.priority
          bestMatch = name
        }
      }
    }
    return bestMatch
  }, [])

  return (
    <StudyRegionContext.Provider value={{ updateRegion, removeRegion, getRegionName, containerRef }}>
      {children}
    </StudyRegionContext.Provider>
  )
}

// `target` is a ref OR a raw element — see resolveNode for why both are needed.
export const useStudyRegion = (regionId, target, priority = 0) => {
  const { updateRegion, removeRegion, containerRef } = useContext(StudyRegionContext)

  useEffect(() => {
    const el = resolveNode(target)
    if (!el) return

    // Helper to calculate and send normalized percentages, relative to the IDE box
    const reportPosition = () => {
      const container = containerRef?.current
      if (!container) return

      const rect = el.getBoundingClientRect()
      const box = container.getBoundingClientRect()
      if (!box.width || !box.height) return

      updateRegion(regionId, {
        top: Number(((rect.top - box.top) / box.height).toFixed(4)),
        bottom: Number(((rect.bottom - box.top) / box.height).toFixed(4)),
        left: Number(((rect.left - box.left) / box.width).toFixed(4)),
        right: Number(((rect.right - box.left) / box.width).toFixed(4)),
        priority: priority
      })
    }

    // 1. Observer: Watches for element size changes
    const observer = new ResizeObserver(() => {
      reportPosition()
    })

    // 2. Start Observing
    observer.observe(el)

    // 3. Window Listener: Watches for browser resize
    window.addEventListener('resize', reportPosition)
    window.addEventListener('scroll', reportPosition) // Handle scroll shifts

    // Initial report
    reportPosition()

    // 4. Cleanup
    return () => {
      observer.disconnect()
      window.removeEventListener('resize', reportPosition)
      window.removeEventListener('scroll', reportPosition)
      removeRegion(regionId)
    }
  }, [regionId, target, containerRef, updateRegion, removeRegion, priority])
}
